import { ArsListing, ArsListingBody, ArsListingDataset, isArsListingBody, PauseCover } from "aic";
import { CommentScope } from "components/common/comments/CommentScope";
import { v3EditorProps } from "components/common/v3EditorProps";
import { HierarchyContext } from "contexts/HierarchyContext";
import { UPDATE_TLF } from "contexts/tlf/interface";
import { TlfContext } from "contexts/TlfContext";
import { IEditorV3, isIEditorV3 } from "editor-v3";
import { eAccessRights } from "functions/api/enums";
import { isTlfItemReadDto } from "functions/api/tlfItem/TlfItemReadDto";
import { useContext } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { ArsSetPicker } from "./ArsSetPicker";

/**
 * Wrapper component for ARSListing.
 */
export const TlfArsListing = () => {
  const hierarchyContext = useContext(HierarchyContext);
  const tlfContext = useContext(TlfContext);
  const tlfData = tlfContext?.tlfData;

  const body =
    tlfContext && isTlfItemReadDto(tlfData) && isArsListingBody(tlfData.body, isIEditorV3)
      ? tlfData.body
      : null;

  const disabled = !hierarchyContext?.accessRights.includes(
    eAccessRights.WriteToTlfItemsInHierarchy,
  );

  const isClinicalLibraryLoading = hierarchyContext?.isClinicalLibraryLoading !== false;

  const datasets: ArsListingDataset[] =
    hierarchyContext && !isClinicalLibraryLoading
      ? [
          ...(hierarchyContext.hierarchy?.clinicalLibrary?.clinicalDatasets ?? []),
          ...hierarchyContext.parentTemplates.clinicalDatasets,
        ].map((dataset) => ({
          id: String(dataset.id),
          name: dataset.name,
          clinicalVariables: dataset.clinicalVariables.map((variable) => ({
            id: String(variable.id),
            name: variable.name,
            dataType: variable.dataType,
            description: variable.description,
            datasetName: dataset.name,
          })),
        }))
      : [];

  return !tlfContext || !body ? null : (
    <Container style={{ minHeight: "400px" }}>
      {isClinicalLibraryLoading ? (
        <Row>
          <Col>
            <PauseCover text="Loading data sets..." />
          </Col>
        </Row>
      ) : datasets.length === 0 ? (
        <p className="w-100 text-danger text-center my-4">
          No clinical library data sets available.
        </p>
      ) : (
        <>
          <Row>
            <Col className="d-flex align-items-start flex-row gap-1 py-3">
              <ArsSetPicker
                type={"analysisSet"}
                itemId={body.analysisSetId}
                onChange={(newId) => {
                  tlfContext.dispatch({
                    operation: UPDATE_TLF,
                    tlfData: {
                      body: {
                        ...body,
                        analysisSetId: newId,
                      },
                    },
                    calledFrom: "ArsListing onChange:AnalysisSet",
                  });
                }}
              />
              <ArsSetPicker
                type={"dataSubset"}
                itemId={body.dataSubsetId ?? ""}
                onChange={(newId) => {
                  tlfContext.dispatch({
                    operation: UPDATE_TLF,
                    tlfData: {
                      body: {
                        ...body,
                        dataSubsetId: newId,
                      },
                    },
                    calledFrom: "ArsListing onChange:DataSubset",
                  });
                }}
              />
            </Col>
          </Row>
          <Row>
            <Col className="d-flex justify-content-center">
              <CommentScope
                id="ars-listing-comment"
                activeContext="TlfItem"
                wrap
                iconPosition={{ top: "16px", left: "38px" }}
                commentLabel="New table comment"
              >
                <ArsListing<IEditorV3>
                  arsListingBody={body}
                  disabled={disabled}
                  datasets={datasets}
                  editorProps={v3EditorProps}
                  justification="center"
                  tlfViewMode={tlfContext!.tlfViewMode}
                  updateArsListingBody={(newBody) =>
                    tlfContext!.dispatch({
                      operation: UPDATE_TLF,
                      tlfData: {
                        body: newBody as ArsListingBody<IEditorV3>,
                      },
                      calledFrom: "TlfArsListing onChange",
                    })
                  }
                />
              </CommentScope>
            </Col>
          </Row>
        </>
      )}
    </Container>
  );
};

TlfArsListing.displayName = "TlfArsListing";
